import { FormEventHandler, ReactNode, useRef } from "react";
import { useDialog } from "@react-aria/dialog";
import Grow from "@avinet/adaptive-ui-core/layout/Grow";
import Row from "@avinet/adaptive-ui-core/layout/Row";
import ModalContainer from "@avinet/adaptive-ui-core/ui/ModalContainer";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import { Icon, IconEnum } from "./Icon";
import { LoadingMask } from "./Loading";
import "./Dialog.scss";

export interface DialogProps {
  icon?: IconEnum;
  variant?: "default" | "success" | "warning" | "danger";
  /** Additional class name(s) on the dialog container element. */
  className?: string;
  /** Additional class name(s) on the dialog content element. */
  contentClassName?: string;
  /**
   * Dialog title. If set, this is rendered as the first child of the content
   * element.
   *
   * This can be a plain string or other content.
   */
  title?: ReactNode | null;
  /** Default width. It will still be constrained by the viewport. */
  width?: number;
  /** Buttons. Use class name .btn and variants to get correct styling. */
  buttons?: ReactNode | null;
  /** Form submit handler. Set this if this dialog is a form. */
  onSubmit?: FormEventHandler<HTMLFormElement>;
  /** If set, the dialog will have a close button. */
  onClose?: () => void;
  /**
   * If set, scrolling the background will be disabled. DO NOT USE on dialogs
   * with forms or input elements.
   */
  scrollDisabled?: boolean;
  children?: ReactNode;
  mask?: boolean | ReactNode;
}

const noSubmit: FormEventHandler<HTMLFormElement> = (e) => e.preventDefault();

/**
 * Renders an overlay and a container with a styled title and `children` as
 * content. This uses react-aria to achieve accessibility, prevent focus
 * escaping the dialog.
 *
 * This variant allows scrolling by default to not mess with input focus
 * behavior on mobile.
 */
export function Dialog({
  icon,
  variant = "default",
  title,
  width = 400,
  children,
  buttons,
  className = "",
  contentClassName,
  onSubmit = noSubmit,
  onClose,
  scrollDisabled = false,
  mask = false,
}: DialogProps) {
  const t = useTranslate("Dialog");
  const ref = useRef<HTMLDivElement>(null);
  const { dialogProps, titleProps } = useDialog({}, ref);

  return (
    <ModalContainer
      className="aui-dialog-overlay"
      scrollEnabled={!scrollDisabled}
    >
      <div
        className={`dialog ${variant} ${className}`}
        style={{ width }}
        ref={ref}
        {...dialogProps}
      >
        <Row className="dialog-header" align="top">
          {icon && (
            <big>
              <Icon
                name={icon}
                className={`featured ${
                  variant === "default" ? "primary" : variant
                }`}
              />
            </big>
          )}
          <Grow />
          {onClose && (
            <button
              type="button"
              className="btn-link icon dark small"
              onClick={onClose}
              title={t("dismiss")}
            >
              <Icon name="dismiss" />
            </button>
          )}
        </Row>

        <form onSubmit={onSubmit}>
          <div className={`dialog-content ${contentClassName ?? ""}`}>
            {title && <h3 {...titleProps}>{title}</h3>}
            {typeof children === "string" ? (
              <p className="pre-wrap">{children}</p>
            ) : (
              children
            )}
          </div>

          {buttons && <div className="btn-group">{buttons}</div>}
        </form>

        {mask ? mask === true ? <LoadingMask /> : mask : null}
      </div>
    </ModalContainer>
  );
}
